import { Fragment, useRef } from 'react'
import PropTypes from 'prop-types'
import Clamp from 'react-multi-clamp'
import { withBreakpoints } from 'react-breakpoints'
import { Spinner, Visible } from '@leroy-merlin-br/backyard-react-ui'

import IconDepartment from 'reactGarden/IconsDepartment'

import { useBodyScrollLock } from 'reactUtils/hooks/use-body-scroll-lock'

import { isLink } from 'lmcv/modules/utils'
import { Icon } from 'lmcv/components/Icon'

import { Info } from './components/Info'
import { TabsInfo } from '../components/Tabs'
import * as S from './styled'

const Modal = ({
  image,
  title,
  subtitle,
  rules,
  description,
  productLink,
  giftedBy,
  category,
  buttonText,
  footerText,
  isActive,
  isLoading,
  isDisabled,
  onClose,
  onClickButton
}) => {
  const scrollElement = useRef()

  useBodyScrollLock(scrollElement)

  const ICON_SYMBOL_MAP = {
    percentage: 'percent',
    money: 'dollar_sign'
  }

  const thumbResolver = (src) => {
    if (isLink(src)) {
      return <img src={src} alt={title} />
    }

    if (src) {
      return <IconDepartment name={src} />
    }

    return <Icon name="dollar_note" />
  }

  const modalContent = () => {
    return (
      <Fragment>
        <Visible untilKilo kilo>
          <TabsInfo
            description={description}
            rules={rules}
            productLink={productLink}
          />
        </Visible>

        <Visible mega giga tera>
          <Info
            title={title}
            description={description}
            rules={rules}
            productLink={productLink}
            giftedBy={giftedBy}
          />
        </Visible>
      </Fragment>
    )
  }

  const footerRender = () => {
    const button = isActive ? buttonText.active : buttonText.default

    return (
      <S.Footer>
        {footerText.expire && <S.TextExpire>{footerText.expire}</S.TextExpire>}

        {buttonText.label && <S.ButtonLabel>{buttonText.label}</S.ButtonLabel>}
        <S.ButtonSend
          color={isActive ? 'purple' : 'green'}
          isDisabled={isDisabled}
          isStretch
          isActive={isActive}
          isLoading={isLoading}
          onClick={onClickButton}
        >
          {isActive && <Icon name="checked" />}
          {isLoading ? <Spinner /> : button}
        </S.ButtonSend>

        {footerText.remaining && (
          <S.TextRemaining>{footerText.remaining}</S.TextRemaining>
        )}
      </S.Footer>
    )
  }

  const headerRender = () => {
    return (
      <S.Header>
        <S.Picture>
          <S.PictureImage>{thumbResolver(image)}</S.PictureImage>

          <S.PictureIcon>
            <Icon name={ICON_SYMBOL_MAP[category]} />
          </S.PictureIcon>
        </S.Picture>

        <S.SubTitle>{subtitle}</S.SubTitle>

        <S.Title>
          <Clamp ellipsis="..." clamp={2}>
            {title}
          </Clamp>
        </S.Title>
      </S.Header>
    )
  }

  return (
    <S.Wrapper isActive={isActive}>
      {giftedBy && (
        <S.TextShare>
          <Icon name="gift" fill="currentColor" />
        </S.TextShare>
      )}
      <S.ButtonClose onClick={onClose} isActive={isActive}>
        <Icon name="close_tiny" />
      </S.ButtonClose>

      <S.ButtonCloseText onClick={onClose}>
        <span>Fechar</span> <Icon name="close_tiny" />
      </S.ButtonCloseText>

      <Visible mega giga tera>
        <S.Aside>
          {headerRender()}

          {footerRender()}
        </S.Aside>
      </Visible>

      <S.ContentScroll ref={scrollElement}>
        <Visible untilKilo kilo>
          <S.Aside>{headerRender()}</S.Aside>
        </Visible>

        <S.Content>{modalContent()}</S.Content>
      </S.ContentScroll>

      <Visible untilKilo kilo>
        {footerRender()}
      </Visible>
    </S.Wrapper>
  )
}

Modal.propTypes = {
  image: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  rules: PropTypes.string,
  description: PropTypes.string,
  productLink: PropTypes.string,
  giftedBy: PropTypes.string,
  category: PropTypes.oneOf(['percentage', 'money']).isRequired,
  buttonText: PropTypes.shape({
    label: PropTypes.string,
    default: PropTypes.string,
    active: PropTypes.string
  }),
  footerText: PropTypes.shape({
    expire: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    active: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
  }).isRequired,
  isActive: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onClickButton: PropTypes.func
}

Modal.defaultProps = {
  buttonText: {
    default: 'Ativar',
    active: 'Ativado'
  },
  isActive: false,
  isLoading: false,
  isDisabled: false
}

export default withBreakpoints(Modal)
